// js/components/ProductItem.js
import Delegate from 'ftdomdelegate';
import fastdom from 'fastdom';
// import { ProductItemColorSwatch } from './product-item-color-swatch.js';
import { ProductSection } from './product-section.js';
import { SecureStorage } from '../utils.js';
export class ProductItem extends HTMLElement {
  constructor() {
    super();
    this.delegateElement = new Delegate(this);
    this.colorSwatch = null;
    this.settings = {};
    this.wishlistData = [];
    this.wishlistOptions = {
      localStorageWishlistKey: "shopify-wishlist",
      localStorageDelimiter: ",",
      buttonActiveClass: "wishlist__added",
    };
    this._attachListeners();
    // this._initializeColorSwatch();
  }

  connectedCallback() {
    this._setupSettings();
    this._setupProductData();
    this._initializeWishlist();
  }

  disconnectedCallback() {
    this.destroy();
  }

  destroy() {
    this.delegateElement.off();
    if (this.colorSwatch) {
      this.colorSwatch.destroy();
    }
    if (this._fixSafariListener) {
      window.removeEventListener("resize", this._fixSafariListener);
    }
  }

  _attachListeners() {
    // Color swatch change handling
    this.delegateElement.on("change", ".product-item__swatch-list .color-swatch__radio", this._handleColorChange.bind(this));
    
    // Wishlist button handling
    this.delegateElement.on("click", ".product-item__wishlist-button", this._handleWishlistClick.bind(this));
    
    // Quick view handling
    this.delegateElement.on("click", "[data-action='open-modal'][data-secondary-action='open-quick-view']", this._handleQuickViewClick.bind(this));
    
    // Product image hover for secondary image
    this.delegateElement.on("mouseenter", ".product-item__image-wrapper", this._handleImageHover.bind(this));
    this.delegateElement.on("mouseleave", ".product-item__image-wrapper", this._handleImageLeave.bind(this));
    
    // Safari fix for image height calculation
    this._fixSafariListener = this._fixSafari.bind(this);
    window.addEventListener("resize", this._fixSafariListener);
  }

  // _initializeColorSwatch() {
  //   const swatchList = this.querySelector(".product-item__swatch-list");
  //   if (swatchList) {
  //     this.colorSwatch = new ProductItemColorSwatch(this);
  //   }
  // }

  _initializeWishlist() {
    // Initialize wishlist data from localStorage
    this.wishlistData = this._getWishlistData();
    
    // Update button state on initialization
    this.updateWishlistButtonState();
  }

  _setupSettings() {
    // Parse settings from data-settings attribute
    const settingsAttribute = this.getAttribute('data-settings');
    if (settingsAttribute) {
      try {
        this.settings = JSON.parse(settingsAttribute);
      } catch (error) {
        this.settings = {};
      }
    }
  }

  _setupProductData() {
    // Extract product data from settings only
    const productId = this.settings.productId;
    const variantId = this.settings.variantId;
    const productHandle = this.settings.productHandle;
    
    if (productId) {
      this.productData = {
        id: productId,
        handle: productHandle,
        variantId: variantId,
        url: this.settings.productUrl || this.querySelector(".product-item__title")?.href,
        imageUrl: this.settings.imageUrl || this.querySelector(".product-item__primary-image")?.src,
        // Include all settings
        ...this.settings
      };
    }
  }

  _handleColorChange(event, target) {
    const variantUrl = target.getAttribute("data-variant-url");
    const mediaId = target.getAttribute("data-media-id");
    const imageUrl = target.getAttribute("data-image-url");
    const imageAspectRatio = target.getAttribute("data-image-aspect-ratio");

    // Update product links
    this._updateProductLinks(variantUrl);
    
    // Update product image if different media
    if (imageUrl && mediaId) {
      this._updateProductImage(imageUrl, mediaId, imageAspectRatio);
    }

    // Dispatch custom event for other components to listen
    this.dispatchEvent(new CustomEvent("product:variant:changed", {
      bubbles: true,
      detail: {
        variantUrl,
        mediaId,
        imageUrl,
        productId: this.productData?.id
      }
    }));
  }

  _updateProductLinks(variantUrl) {
    const imageWrapper = this.querySelector(".product-item__image-wrapper");
    const titleLink = this.querySelector(".product-item__title");
    
    if (imageWrapper && variantUrl) {
      imageWrapper.setAttribute("href", variantUrl);
    }
    
    if (titleLink && variantUrl) {
      titleLink.setAttribute("href", variantUrl);
    }
  }

  _updateProductImage(imageUrl, mediaId, imageAspectRatio) {
    const originalImage = this.querySelector(".product-item__primary-image");
    const aspectRatioContainer = originalImage?.parentNode;
    
    if (!originalImage || !aspectRatioContainer) return;
    
    // Check if we need to update the image
    if (originalImage.getAttribute("data-media-id") === mediaId) return;
    
    fastdom.mutate(() => {
      const newImage = document.createElement("img");
      newImage.className = "product-item__primary-image";
      newImage.setAttribute("data-media-id", mediaId);
      newImage.setAttribute("src", imageUrl);
      newImage.setAttribute("loading", "lazy");
      
      // Update aspect ratio
      if (imageAspectRatio) {
        aspectRatioContainer.style.paddingBottom = `${100 / imageAspectRatio}%`;
      }
      
      // Replace the image
      aspectRatioContainer.replaceChild(newImage, originalImage);
    });
  }

  _handleWishlistClick(event) {
    event.preventDefault();
    event.stopPropagation();
    
    const button = event.target.closest(".product-item__wishlist-button");
    const productHandle = this.settings.productHandle;
    
    if (!productHandle) {
      return;
    }
    
    // Reload wishlist data from storage before updating
    this.wishlistData = this._getWishlistData();
    
    // Update wishlist data
    this._updateWishlistData(productHandle);
    
    // Toggle button state
    const isInWishlist = this._isInWishlistData(productHandle);
    button.classList.toggle(this.wishlistOptions.buttonActiveClass, isInWishlist);
    
    // Update header wishlist count
    this._updateHeaderWishlistCount();
    
    // Dispatch custom event for other components to listen
    this.dispatchEvent(new CustomEvent("product:wishlist:toggle", {
      bubbles: true,
      detail: {
        productHandle,
        isInWishlist,
        button,
        settings: this.settings
      }
    }));
  }

  _handleQuickViewClick(event) {
    event.preventDefault();
    event.stopPropagation();
    
    const button = event.target;
    const productUrl = button.getAttribute("data-product-url");
    const modalId = button.getAttribute("aria-controls");
    
    this._openQuickView(productUrl, modalId);
  }

  _openQuickView(productUrl, modalId) {
    const url = new URL(`${window.location.origin}${productUrl}`);
    
    // Check if mobile/tablet - redirect to product page
    if (window.matchMedia("(max-width: 699px)").matches || window.matchMedia("(max-width: 999px)").matches) {
      window.location.href = url.href;
      return;
    }
    
    const modal = document.getElementById(modalId);
    if (!modal) return;
    
    modal.classList.add("is-loading");
    
    fetch(url.href, {
      credentials: "same-origin",
      method: "GET"
    }).then((response) => {
      response.text().then((content) => {
        const fragment = document.createRange().createContextualFragment(content);
        const modalContent = fragment.querySelector('[data-section-type="product-quick-view"]');
        
        if (modalContent) {
          modal.querySelector(".modal__inner").replaceChildren(modalContent);
          modal.classList.remove("is-loading");
          
          // Initialize ProductSection for the modal content
          const modalProductSection = new ProductSection(modalContent);
          const doCleanUp = () => {
            if (modalProductSection.onUnload) {
              modalProductSection.onUnload();
            }
            modal.removeEventListener("modal:closed", doCleanUp);
          };
          modal.addEventListener("modal:closed", doCleanUp);
        }
      });
    }).catch((error) => {
      modal.classList.remove("is-loading");
    });
  }

  _handleImageHover(event) {
    const secondaryImage = this.querySelector(".product-item__secondary-image");
    if (secondaryImage) {
      fastdom.mutate(() => {
        secondaryImage.style.opacity = "1";
      });
    }
  }

  _handleImageLeave(event) {
    const secondaryImage = this.querySelector(".product-item__secondary-image");
    if (secondaryImage) {
      fastdom.mutate(() => {
        secondaryImage.style.opacity = "0";
      });
    }
  }

  // Public methods for external use
  updateVariant(variantData) {
    if (!variantData) return;
    
    // Update price if provided
    if (variantData.price) {
      this._updatePrice(variantData.price, variantData.compare_at_price);
    }
    
    // Update availability
    if (variantData.available !== undefined) {
      this._updateAvailability(variantData.available);
    }
    
    // Update variant ID in forms
    this._updateVariantId(variantData.id);
  }

  _updatePrice(price, compareAtPrice) {
    const priceElement = this.querySelector(".price--highlight");
    const comparePriceElement = this.querySelector(".price--compare");
    
    if (priceElement) {
      priceElement.textContent = this._formatPrice(price);
    }
    
    if (comparePriceElement && compareAtPrice) {
      comparePriceElement.textContent = this._formatPrice(compareAtPrice);
      comparePriceElement.style.display = "inline";
    } else if (comparePriceElement) {
      comparePriceElement.style.display = "none";
    }
  }

  _updateAvailability(available) {
    const addToCartButton = this.querySelector(".product-item__action-button");
    const inventoryElement = this.querySelector(".product-item__inventory");
    
    if (addToCartButton) {
      if (available) {
        addToCartButton.removeAttribute("disabled");
        addToCartButton.classList.remove("button--disabled");
      } else {
        addToCartButton.setAttribute("disabled", "disabled");
        addToCartButton.classList.add("button--disabled");
      }
    }
    
    if (inventoryElement) {
      inventoryElement.textContent = available ? 
        window.languages?.inStock || "In Stock" : 
        window.languages?.soldOut || "Sold Out";
    }
  }

  _updateVariantId(variantId) {
    const hiddenInput = this.querySelector('input[name="id"]');
    if (hiddenInput) {
      hiddenInput.value = variantId;
    }
  }

  _formatPrice(price) {
    // Basic price formatting - can be enhanced based on shop currency settings
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price / 100);
  }

  // Method to recalculate color swatches (called by parent components)
  recalculateSwatches() {
    if (this.colorSwatch) {
      this.colorSwatch.recalculateSwatches();
    }
  }

  // Method to check if product is in wishlist
  isInWishlist() {
    const productHandle = this.settings.productHandle;
    return productHandle ? this._isInWishlistData(productHandle) : false;
  }

  // Method to update wishlist button state
  updateWishlistButtonState() {
    const button = this.querySelector(".product-item__wishlist-button");
    if (button) {
      const isInWishlist = this.isInWishlist();
      button.classList.toggle(this.wishlistOptions.buttonActiveClass, isInWishlist);
    }
  }

  // Update header wishlist count
  _updateHeaderWishlistCount() {
    const headerCount = document.querySelector('[data-wishlist-count-header]');
    if (headerCount) {
      headerCount.textContent = this.wishlistData.length;
    }
  }

  /**
   * On Safari 11.1 and lower, the browser incorrectly calculate the height of flex and grid items due to a bug
   * on how padding percentage is calculated (that we use for allocating image space). This is solved in Safari 11.1 and higher.
   *
   * For those browsers, we fix that in JavaScript by setting the height directly for each aspect ratio image
   *
   * @private
   */
  _fixSafari() {
    const userAgent = window.navigator.userAgent.toLowerCase();
    if (userAgent.includes("safari") && (userAgent.includes("version/10.1") || userAgent.includes("version/10.3") || userAgent.includes("version/11.0"))) {
      const isPhone = window.matchMedia("(max-width: 699px)").matches;
      this.querySelectorAll(".product-item__image-wrapper .aspect-ratio, .product-item__image-wrapper .placeholder-svg").forEach((image) => {
        if (isPhone) {
          image.parentNode.style.height = null;
        } else {
          image.parentNode.style.height = `${image.clientHeight}px`;
        }
      });
    }
  }

  // Wishlist helper methods
  _getWishlistData() {
    try {
      // Try to get JSON format first
      const jsonData = SecureStorage.getJSON(this.wishlistOptions.localStorageWishlistKey, null);
      if (jsonData !== null) {
        return jsonData;
      }
      
      // Fallback: try to get old comma-separated format
      const stored = SecureStorage.getItem(this.wishlistOptions.localStorageWishlistKey);
      if (stored) {
        const items = stored.split(this.wishlistOptions.localStorageDelimiter);
        // Migrate to JSON format
        SecureStorage.setJSON(this.wishlistOptions.localStorageWishlistKey, items);
        return items;
      }
      
      return [];
    } catch (error) {
      return [];
    }
  }

  _isInWishlistData(handle) {
    return this.wishlistData.indexOf(handle) !== -1;
  }

  _updateWishlistData(handle) {
    const indexInWishlist = this.wishlistData.indexOf(handle);
    if (indexInWishlist === -1) {
      this.wishlistData.push(handle);
    } else {
      this.wishlistData.splice(indexInWishlist, 1);
    }
    this._saveWishlistData();
  }

  _saveWishlistData() {
    const wishlistLength = this.wishlistData.length;
    
    try {
      if (wishlistLength) {
        SecureStorage.setJSON(this.wishlistOptions.localStorageWishlistKey, this.wishlistData);
      } else {
        SecureStorage.removeItem(this.wishlistOptions.localStorageWishlistKey);
      }
    } catch (error) {
      // Silent error handling
    }

    // Sync with customer account if logged in
    if (window.customer && window.customer.loggedIn) {
      this._syncWithCustomer();
    }
  }

  _syncWithCustomer() {
    // Sync wishlist with customer account via API
    if (window.customer && window.customer.loggedIn && window.customer.id) {
      const url = "/apps/ff-app/whishlist";
      const handles = this.wishlistData.join(',');
      
      const formData = new FormData();
      formData.append("customerId", window.customer.id);
      formData.append("handles", handles);
      
      fetch(url, {
        method: "POST",
        body: formData,
        credentials: "same-origin"
      })
        .then((response) => {
          if (response.ok) {
            return response.json();
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        })
        .then((data) => {
          // Update window.customer with fresh data
          if (data.success && data.handles) {
            window.customer.wishlist.handles = data.handles.split(',');
            window.customer.wishlist.count = data.handles.split(',').length;
          }
        })
        .catch((error) => {
          // Silent error handling
        });
    }
  }
}

if (!window.customElements.get("cos-product-item")) window.customElements.define("cos-product-item", ProductItem);
